import * as React from 'react';
import { AgentWelcome, AgentMessageBubble, AgentComposerInput, type AgentConversationPresentation } from './agent-chat-presentation';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { StrategicBriefChips } from '@/components/crm/strategic-brief-chips';
import {
  ApiError,
  type SowSession,
  type WorkspaceEvidenceBinding,
  type AgentAccessScope,
  type AgentConversation as SavedConversation,
  type AgentLibraryEntry,
  type AgentPromptSnapshot,
  getListAgentConversationsQueryKey,
  getGetAgentConversationQueryKey,
  getListAgentMessagesQueryKey,
  useGetAgentConversation,
  useListAgentMessages,
  type AgentActionExecution,
  type AgentActionProposal,
  type AgentChatResponse,
  type AgentContinuation,
  getAgentProposal,
  getAgentExecution,
  useGetAgentExecution,
  getGetAgentExecutionQueryKey,
  type PersonalWorkItem,
  type AgentChatRequest,
  type AgentClarificationAnswerBundle,
  type AgentClarificationGroup,
  type AgentContextResponse,
  type AgentSource,
  type DraftHandoff,
  useCancelAgentProposal,
  useChatWithAgent,
  useGetAgentContext,
  useGetAgentResearchStatus,
  type ResearchEvidence,
  useReviewAgentProposal,
  useUndoAgentExecution
} from '@cdf/api-client-react';
import {
  ArrowDownIcon,
  BotIcon,
  CheckCircle2Icon,
  ExternalLinkIcon,
  Loader2Icon,
  ShieldCheckIcon,
  XCircleIcon
} from 'lucide-react';

import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { useSession } from '@/components/auth/user-provider';
import { useQueryClient } from '@tanstack/react-query';
import { AgentSavedChats } from './agent-saved-chats';
import { AgentLinkedReply } from './agent-linked-reply';
import { SharedAgentShell } from './shared-agent-shell';
import type { SharedAgentRecord } from './shared-chat-context';
import { useAgentAttachments } from './use-agent-attachments';
import { AgentAttachments, attachmentFooterErrors, PrivateAttachmentCitation } from './agent-attachments';
import type { AgentDraftReceiver, AgentToolRouteResult } from './agent-tool-routing';
import { AgentFooter } from './agent-footer';
import { useAgentScroll } from './use-agent-scroll';
import { useAgentRailLayout } from './use-agent-rail-layout';
import { AgentEntityPicker } from './agent-entity-picker';
import { AgentPromptLibrary } from './agent-prompt-library';
import { promptContextForRoute } from './prompt-library-context';
import { usePromptTargetOptions } from './use-prompt-target-options';
import { agentWorkspaceParams, storedAgentTurns, workspaceError } from './workspace-utils';
import { useWorkspace } from '@/components/workspace/workspace-provider';
import { AgentClarificationQuestions } from '@/components/agent/agent-clarification-questions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ResearchMarkdown } from '@/components/research/research-message';
import {
  browserSpeechSynthesisSupported,
  browserTranscriptionSupported,
  speakBrowserReply,
  startBrowserTranscription
} from '@/lib/browser-voice';
import { cn } from '@/lib/utils';
import { WorkspaceAgentCitation } from '@/components/document-workspaces/agent-citation';
import { SowExactReview } from '@/components/sow/sow-exact-review';
import { useSowRequest } from '@/components/sow/sow-request';
import { useDraftNavigationGuard, useGuardedNavigate } from '@/hooks/use-draft-navigation-guard';
import { useKeyboardInset } from '@/hooks/use-keyboard-inset';
import { useAuth } from '@/components/auth/auth-provider';
import { AgentContinuationCard } from './agent-continuation-card';
import { AgentEnquiryJourney } from './agent-enquiry-journey';
import { AgentContentJourney } from './agent-content-journey';
import { stageAgentNativePrefill } from './agent-native-prefill';
import { FollowUpDrawer } from '@/components/next-actions/follow-up-suggestions';
import { PersonalWorkDrawer } from '@/components/personal-work/personal-work-inbox';
import { sameWorkScope, workResumeLink, type WorkResumeReceiver } from '@/components/personal-work/work-resume';
import { AgentWorkCard, useAgentDeadline, workCardChanged, workCardKey, workCardValues, type WorkCardValues } from './agent-work-card';

import { AgentTenderPreparation } from './agent-tender-preparation';
import { AgentLifecycleReply } from './agent-lifecycle-reply';
import { AgentSuggestedSteps } from './agent-suggested-steps';
import { LIFECYCLE_NAVIGATION_EVIDENCE } from '@cdf/navigation-catalogue';

type AgentTurn = {
  tenderPreparationId?: string;  id: string;
  requestId?: string;
  role: 'user' | 'agent';
  content: string;
  sources?: AgentSource[];
  webEvidence?: ResearchEvidence[];
  draftHandoffs?: DraftHandoff[];
  proposals?: AgentActionProposal[];
  continuations?: AgentContinuation[];
  suggestedSteps?: AgentChatResponse['suggestedSteps'];
  historical?: boolean;
  superseded?: boolean;
  evidenceState?: AgentChatResponse['evidenceState'];
  evidenceMessage?: string;
  clarification?: AgentClarificationGroup | null;
  clarificationAnswered?: boolean;
  clarificationGroupId?: string;
};

type AgentSubmission = {
  message: string;
  voice?: boolean;
  prompt?: AgentPromptSnapshot;
  clarificationAnswers?: AgentClarificationAnswerBundle;
  continuation?: AgentChatRequest['continuation'];
  openResult?: boolean;
  nativeAfterDetails?: boolean;
};

type AgentMode = AgentAccessScope['kind'];

type AgentContextReadiness =
  | { status: 'inactive' }
  | { status: 'loading'; scopeKey: string }
  | { status: 'error'; scopeKey: string; message: string }
  | { status: 'ready'; scopeKey: string; data: AgentContextResponse };

function agentScopeKey(scope: AgentAccessScope): string {
  return [scope.kind, scope.tenantId ?? '', scope.entityId ?? ''].join(':');
}

function routeContext(pathname: string) {
  const match = pathname.match(/^\/partner-engagement\/(accounts|contacts|opportunities)\/([0-9a-f-]{36})/i);
  if (!match) return {};
  if (match[1] === 'accounts') return { accountId: match[2] };
  if (match[1] === 'contacts') return { contactId: match[2] };
  return { opportunityId: match[2] };
}

export function suggestedAgentMode(pathname: string): AgentMode {
  if (pathname === '/admin/platform' || pathname.startsWith('/admin/platform/')) return 'platform';
  if (pathname.startsWith('/admin/') && !pathname.startsWith('/admin/entity')) return 'tenant';
  return 'entity';
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'FEATURE_DISABLED') return 'Project AI is not enabled for this project.';
    if (error.code === 'INSUFFICIENT_USAGE_TOKENS') return error.message;
    if (error.code === 'AGENT_PROPOSAL_STALE') return 'The target or your access changed. Ask Agent to prepare a fresh review.';
    if (error.code === 'AGENT_PROPOSAL_EXPIRED') return 'This review expired. Ask Agent to prepare it again.';
    return error.message;
  }
  return 'Agent could not answer. Check the project LLM and AI authority settings, then try again.';
}

function errorCode(error: unknown): string | undefined {
  return error instanceof ApiError ? error.code : undefined;
}

function contextErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Agent access could not be checked. Try again or choose another authorized mode.';
}

export function AgentSources({ items, privateContext }: { items: AgentSource[]; privateContext?: { scope: AgentAccessScope; conversationId: string; identity: string } }) {
  if (items.length === 0) return null;
  const briefs = items.filter((source) => source.kind === 'strategic_brief' && source.deepLink);
  const others = items.filter((source) => !briefs.includes(source));
  return (
    <>
      <StrategicBriefChips
        className="mt-3"
        sources={briefs.map((source) => ({
          briefId: source.id,
          recordName: source.sourceIdentity?.replace(/ — Strategic brief$/, '') ?? source.label,
          deepLink: source.deepLink ?? '',
          truncated: source.location?.includes('(shortened)') ?? false
        }))}
      />
      {others.length > 0 && <AgentSourceList items={others} privateContext={privateContext} />}
    </>
  );
}

function AgentSourceList({ items, privateContext }: { items: AgentSource[]; privateContext?: { scope: AgentAccessScope; conversationId: string; identity: string } }) {
  if (items.length === 0) return null;
  return (
    <details className="mt-3 rounded-md border bg-background/60 p-2 text-xs">
      <summary className="cursor-pointer font-medium">CRM and document citations ({items.length})</summary>
      <div className="mt-2 space-y-1.5">
        {items.map((source) => {
          if (source.sourceType === 'conversation_attachment' && privateContext) return <PrivateAttachmentCitation key={source.id + ':' + source.citation} source={source} {...privateContext} />;
          if (source.sourceType === 'workspace_evidence') return <WorkspaceAgentCitation key={source.id + ':' + source.citation} source={source} />;
          const content = (
            <>
              <Badge variant="outline">[{source.citation}]</Badge>
              <span className="min-w-0 flex-1">
                <span className="block truncate">{source.sourceIdentity}</span>
                <span className="block text-[11px] text-muted-foreground">
                  {source.location}{source.freshness ? ' · ' + new Date(source.freshness).toLocaleString() : ''}
                </span>
              </span>
              {source.deepLink && <ExternalLinkIcon className="size-3.5 shrink-0" />}
            </>
          );
          return source.deepLink ? (
            <Link key={source.id + ':' + source.citation} to={source.deepLink} className="flex items-center gap-2 rounded p-1.5 hover:bg-muted">
              {content}
            </Link>
          ) : (
            <div key={source.id + ':' + source.citation} className="flex items-center gap-2 rounded p-1.5">{content}</div>
          );
        })}
      </div>
    </details>
  );
}

export function AgentDraftHandoffs({ items }: { items: DraftHandoff[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3 space-y-1.5 border-t pt-2">
      <p className="text-xs font-medium">Secure handoffs</p>
      {items.map((handoff) => (
        <Button key={handoff.channel + ':' + handoff.label} variant="outline" size="sm" asChild className="w-full justify-between">
          <Link to={handoff.deepLink}>{handoff.label}<ExternalLinkIcon className="size-3.5" /></Link>
        </Button>
      ))}
      <p className="text-[11px] text-muted-foreground">Opening a workspace does not save, send, publish, or connect anything.</p>
    </div>
  );
}

function confirmedResultLink(execution: AgentActionExecution): string | null {
  const link = execution.receipt?.link;
  return execution.status === 'succeeded' && typeof link === 'string' && /^\/(?!\/)[a-zA-Z0-9/?=&._-]+$/.test(link) ? link : null;
}
const INSTANT_CHANGE_REASON = 'Instant low-risk change requested in chat; undo available for 24 hours.';
const UNDO_RECEIPT_KEYS = new Set(['link', 'undoId', 'undoExpiresAt', 'autonomyTier']);

function UndoInstantChange({ undoId, expiresAt }: { undoId: string; expiresAt: string | null }) {
  const undo = useUndoAgentExecution();
  const expired = expiresAt ? Date.parse(expiresAt) <= Date.now() : false;
  if (undo.isSuccess) return <p className="mt-2 font-medium" role="status">Undone — the change was reversed.</p>;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button type="button" size="sm" variant="outline" disabled={expired || undo.isPending}
        onClick={() => undo.mutate({ id: undoId })}>
        {undo.isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : null}Undo
      </Button>
      <span className="text-muted-foreground">{expired ? 'Undo window has ended.' : 'Available for 24 hours.'}</span>
      {undo.isError ? <span role="alert" className="text-destructive">{errorMessage(undo.error)}</span> : null}
    </div>
  );
}

function ExecutionReceipt({ execution }: { execution: AgentActionExecution }) {
  const link = confirmedResultLink(execution);
  const undoId = typeof execution.receipt?.undoId === 'string' ? execution.receipt.undoId : null;
  const undoExpiresAt = typeof execution.receipt?.undoExpiresAt === 'string' ? execution.receipt.undoExpiresAt : null;
  return (
    <div className={cn(
      'mt-3 rounded-md border p-3 text-xs',
      execution.status === 'succeeded' ? 'bg-background' : 'border-destructive/40'
    )} role="status">
      <div className="flex items-center gap-2 font-medium">
        {execution.status === 'succeeded'
          ? <CheckCircle2Icon className="size-4" />
          : <XCircleIcon className="size-4" />}
        Execution {execution.status}
      </div>
      {execution.errorMessage ? <p className="mt-1 text-destructive">{execution.errorMessage}</p> : null}
      {execution.receipt ? (
        <dl className="mt-2 grid gap-1">
          {Object.entries(execution.receipt).filter(([key]) => !UNDO_RECEIPT_KEYS.has(key)).map(([key, value]) => (
            <div key={key} className="flex gap-2"><dt className="font-medium">{key.replaceAll(/([A-Z])/g, ' $1')}:</dt><dd>{String(value)}</dd></div>
          ))}
        </dl>
      ) : null}
      {link ? <Button asChild variant="link" size="sm" className="mt-1 h-auto p-0"><Link to={link}>Open confirmed result</Link></Button> : null}
      {undoId && execution.status === 'succeeded' ? <UndoInstantChange undoId={undoId} expiresAt={undoExpiresAt} /> : null}
    </div>
  );
}

function ResumedWorkReceipt({ item, identity }: { item: PersonalWorkItem; identity: string }) {
  const id = item.resume.executionId ?? '';
  const receipt = useGetAgentExecution(id, { query: {
    queryKey: [...getGetAgentExecutionQueryKey(id), identity, agentScopeKey(item.scope)],
    enabled: Boolean(id), retry: false, staleTime: 0, gcTime: 0
  } });
  return <section aria-label="Original work outcome" className="shrink-0 space-y-2 border-b p-3 text-sm">
    <p className="font-medium">{item.title} · {item.state.replaceAll('_', ' ')}</p>
    <p>{item.nextAction}</p>
    {item.resume.requiresFreshRequest && <p>A fresh request is required; this does not renew the old review.</p>}
    {id && <><p className="break-all text-xs text-muted-foreground">Original execution {id}</p>
      {receipt.isPending ? <p role="status">Reading the original receipt…</p> : receipt.isError ? <p role="alert">The original outcome could not be read. Nothing was submitted again.</p>
        : receipt.data?.id === id ? <ExecutionReceipt execution={receipt.data} /> : null}
      <Button size="sm" variant="outline" disabled={receipt.isFetching} onClick={() => { void receipt.refetch(); }}>Read original outcome again</Button></>}
  </section>;
}

export function AgentProposalReviewCard({
  proposal,
  onFinal,
  historical = false,
  disabled = false,
  openAfterSave = false,
  onOpenResult,
  onBusyChange,
  initialExecution
}: {
  proposal: AgentActionProposal;
  onFinal: (proposalId: string, execution?: AgentActionExecution, terminalStatus?: AgentActionProposal['status']) => void;
  historical?: boolean;
  disabled?: boolean;
  openAfterSave?: boolean;
  onOpenResult?: (execution: AgentActionExecution) => void;
  onBusyChange?: (id: string, busy: boolean) => void;
  initialExecution?: AgentActionExecution;
}) {
  const expired = useAgentDeadline(proposal.expiresAt);
  const [reason, setReason] = React.useState('');
  const [execution, setExecution] = React.useState<AgentActionExecution | undefined>(initialExecution);
  const [uncertain, setUncertain] = React.useState(false);
  const [recovering, setRecovering] = React.useState(false);
  const [canCancelUncertain, setCanCancelUncertain] = React.useState(false);
  const [recoveryError, setRecoveryError] = React.useState<string>();
  const attempted = React.useRef(false);
  const submitting = React.useRef(false);
  const openIntent = React.useRef(false);
  const fence = useSowRequest();
  const requestToken = React.useRef(0);
  const review = useReviewAgentProposal({ request: fence.request, mutation: {
    retry: false,
    onSuccess: (result) => {
      if (!fence.active(requestToken.current)) return;
      submitting.current = false;
      setExecution(result);
      const pending = ['queued', 'running', 'partial', 'indeterminate'].includes(result.status);
      setUncertain(pending);
      onBusyChange?.(proposal.id, pending);
      onFinal(proposal.id, result);
      if (openIntent.current && result.status === 'succeeded' && result.replayed === false) onOpenResult?.(result);
      openIntent.current = false;
    },
    onError: () => {
      if (!fence.active(requestToken.current)) return;
      submitting.current = false;
      openIntent.current = false;
      setUncertain(true);
      onBusyChange?.(proposal.id, true);
    }
  } });
  const cancel = useCancelAgentProposal({ request: fence.request, mutation: {
    retry: false,
    onSuccess: () => { if (fence.active(requestToken.current)) { submitting.current = false; setUncertain(false); setCanCancelUncertain(false); setRecoveryError(undefined); onBusyChange?.(proposal.id, false); onFinal(proposal.id); } },
    onError: () => { if (fence.active(requestToken.current)) { submitting.current = false; setUncertain(true); onBusyChange?.(proposal.id, true); } }
  } });
  const final = proposal.status !== 'prepared' || Boolean(execution) || cancel.isSuccess;
  const reviewExpired = expired && !execution && !uncertain && !proposal.executionId;
  const instant = proposal.autonomyTier === 'instant';
  const autoRan = React.useRef(false);
  // Instant (low-risk, reversible) changes run as soon as they are proposed; the receipt offers Undo for 24 hours.
  React.useEffect(() => {
    if (!instant || final || historical || reviewExpired || disabled || uncertain || autoRan.current || submitting.current) return;
    autoRan.current = true; submitting.current = true; attempted.current = true; openIntent.current = false;
    onBusyChange?.(proposal.id, true); requestToken.current = fence.token();
    review.mutate({ id: proposal.id, data: { previewHash: proposal.previewHash, reason: INSTANT_CHANGE_REASON } });
  }, [instant, final, historical, reviewExpired, disabled, uncertain, onBusyChange, proposal.id, proposal.previewHash, fence, review]);
  async function recover() {
    if (recovering) return;
    const token = fence.token();
    setRecovering(true); setRecoveryError(undefined);
    openIntent.current = false;
    try {
      const current = await getAgentProposal(proposal.id, fence.request);
      if (!fence.active(token)) return;
      if (current.executionId) {
        const result = await getAgentExecution(current.executionId, fence.request);
        if (!fence.active(token)) return;
        setExecution(result);
        const pending = ['queued', 'running', 'partial', 'indeterminate'].includes(result.status);
        setUncertain(pending); onBusyChange?.(proposal.id, pending);
        onFinal(proposal.id, result);
      } else if (['cancelled', 'expired', 'stale', 'failed'].includes(current.status)) {
        setUncertain(false); onBusyChange?.(proposal.id, false); onFinal(proposal.id, undefined, current.status);
      } else if (attempted.current || current.status === 'executing') {
        // A prepared read cannot prove the original POST will not claim execution later.
        setUncertain(true); onBusyChange?.(proposal.id, true);
        setCanCancelUncertain(current.status === 'prepared');
        setRecoveryError('The original confirmation may still finish. Check again for its final receipt.');
      }
    } catch (error) { if (fence.active(token)) setRecoveryError(errorMessage(error)); }
    finally { if (fence.active(token)) setRecovering(false); }
  }
  React.useLayoutEffect(() => {
    if (proposal.status === 'executing') { setUncertain(true); onBusyChange?.(proposal.id, true); }
    // Persisted executing reviews fence new work before their first recovery read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.id]);
  React.useEffect(() => {
    if (historical || proposal.executionId || proposal.status === 'executing') void recover();
    // Historical reads never submit confirmation or arm navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.id]);
  return (
    <section className="mt-3 rounded-md border bg-background p-3 text-xs" aria-label={'Review ' + proposal.review.title}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{proposal.review.title}</p>
          <p className="mt-1 text-muted-foreground">{proposal.review.summary}</p>
        </div>
        <Badge variant={proposal.review.destructive ? 'destructive' : 'outline'}>{proposal.status}</Badge>
      </div>
      <div className="mt-3 grid gap-3">
        <div><p className="font-medium">Exact targets</p><ul className="mt-1 list-disc pl-4">{proposal.review.targets.map((item) => <li key={item}>{item}</li>)}</ul></div>
        {proposal.review.changes.length ? (
          <div>
            <p className="font-medium">Proposed changes</p>
            <div className="mt-1 overflow-x-auto">
              <table className="w-full text-left">
                <thead><tr className="border-b"><th className="py-1 pr-2">Field</th><th className="py-1 pr-2">Before</th><th className="py-1">After</th></tr></thead>
                <tbody>{proposal.review.changes.map((change) => (
                  <tr key={change.field} className="border-b last:border-0">
                    <td className="py-1 pr-2 font-medium">{change.field}</td>
                    <td className="py-1 pr-2 whitespace-pre-wrap">{change.before ?? '—'}</td>
                    <td className="py-1 whitespace-pre-wrap">{proposal.actionKey === 'entity.sow.save_draft' && change.field === 'Exact SOW draft' && change.after ? <SowExactReview serialized={change.after} /> : change.after ?? '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        ) : null}
        <div><p className="font-medium">Side effects</p><ul className="mt-1 list-disc pl-4">{proposal.review.sideEffects.map((item) => <li key={item}>{item}</li>)}</ul></div>
        <div><p className="font-medium">Requirements</p><ul className="mt-1 list-disc pl-4">{proposal.review.requirements.map((item) => <li key={item}>{item}</li>)}</ul></div>
        <div><p className="font-medium">Costs</p><ul className="mt-1 list-disc pl-4">{proposal.review.costs.map((item) => <li key={item}>{item}</li>)}</ul></div>
      </div>
      {reviewExpired && <p role="status" className="mt-3 text-muted-foreground">This exact review expired. Prepare a fresh review before saving.</p>}
      {instant && !final && !historical && !reviewExpired ? (
        <p role="status" className="mt-3 flex items-center gap-2 text-muted-foreground">
          {review.isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : null}
          Low-risk change — applying now. You can undo it for 24 hours.
        </p>
      ) : null}
      {!instant && proposal.autonomyReason && !final ? <p className="mt-3 text-muted-foreground">Needs your confirmation: {proposal.autonomyReason}</p> : null}
      {instant && review.isError ? <p role="alert" className="mt-2 text-destructive">{errorMessage(review.error)}</p> : null}
      {!final && !historical && !reviewExpired && !instant ? (
        <div className="mt-3 space-y-2 border-t pt-3">
          <Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Reason for this change" maxLength={1000} aria-label="Review reason" />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={disabled || uncertain || reason.trim().length < 3 || review.isPending || cancel.isPending}
              onClick={() => { if (submitting.current) return; submitting.current = true; openIntent.current = openAfterSave && !attempted.current; attempted.current = true; onBusyChange?.(proposal.id, true); requestToken.current = fence.token(); review.mutate({ id: proposal.id, data: { previewHash: proposal.previewHash, reason: reason.trim() } }); }}
            >
              {review.isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : <ShieldCheckIcon className="size-3.5" />}
              Confirm exact change
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || uncertain || reason.trim().length < 3 || review.isPending || cancel.isPending}
              onClick={() => { if (submitting.current) return; submitting.current = true; openIntent.current = false; onBusyChange?.(proposal.id, true); requestToken.current = fence.token(); cancel.mutate({ id: proposal.id, data: { reason: reason.trim() } }); }}
            >
              Cancel proposal
            </Button>
          </div>
          {review.isError || cancel.isError ? <p role="alert" className="text-destructive">{errorMessage(review.error ?? cancel.error)}</p> : null}
        </div>
      ) : null}
      {historical && !final && <p className="mt-3 text-muted-foreground">Ask Agent for a fresh review before saving this historical proposal.</p>}
      {(uncertain || historical || proposal.executionId) && <div className="mt-3 space-y-2">
        {uncertain && <p role="status">The save outcome is not confirmed. Check it before continuing; nothing will be submitted again.</p>}
        <Button type="button" variant="outline" size="sm" disabled={recovering || review.isPending || cancel.isPending} onClick={() => void recover()}>Check save outcome</Button>
        {uncertain && canCancelUncertain && <Button type="button" variant="outline" size="sm" disabled={recovering || cancel.isPending || reason.trim().length < 3} onClick={() => { if (submitting.current) return; submitting.current = true; openIntent.current = false; requestToken.current = fence.token(); cancel.mutate({ id: proposal.id, data: { reason: reason.trim() } }); }}>Cancel unresolved proposal</Button>}
        {recoveryError && <p role="alert">{recoveryError}</p>}
      </div>}
      {execution ? <ExecutionReceipt execution={execution} /> : null}
    </section>
  );
}

export type { AgentConversationPresentation } from './agent-chat-presentation';

interface AgentConversationProps {
  record?: SharedAgentRecord;
  /** Account/contact the assistant is focused on (for example the ⌘J queue item); the current record page takes priority. */
  recordIds?: { accountId?: string; contactId?: string };
  presentation?: AgentConversationPresentation;
  onWorkResumeReceiverChange?: (receiver: WorkResumeReceiver | null) => void;
  scopeContainer?: HTMLElement | null;
  workspaceContext?: WorkspaceEvidenceBinding;
  sowSession?: SowSession;
  sowBlocked?: boolean;
  onSowChange?: (session: SowSession) => void;
  compact?: boolean;
  fillHeight?: boolean;
  chatOnly?: boolean;
  researchMode?: 'internal' | 'public_research';
  initialMessage?: string;
  /** Where the Agent was opened (page/record), attached to starter prompts so the answer fits that area. */
  starterContext?: string;
  onToolRequest?: (message: string) => AgentToolRouteResult;
  onDraftReceiverChange?: (receiver: AgentDraftReceiver | null) => void;
  onActionFinal?: () => void;
  onLeaveGuardChange?: (guard: (() => boolean) | null) => void;
}
export function AgentConversation({ record, recordIds, presentation, starterContext, compact = false, researchMode = 'internal', initialMessage, onToolRequest, onDraftReceiverChange, onActionFinal, sowSession, sowBlocked, onSowChange, workspaceContext, onLeaveGuardChange, fillHeight = false, chatOnly = false, scopeContainer, onWorkResumeReceiverChange }: AgentConversationProps) {
  const { session } = useSession();
  const auth = useAuth();
  const access = useTenantEntity();
  const { mode } = useWorkspace();
  const identity = session.status === 'signedIn' ? session.user.email || session.user.name : session.status;
  const boundary = [auth.account?.stableId ?? '', identity, access.selected?.tenantId ?? '', access.selected?.entityId ?? '', mode].join(':');
  if (record) {
    if (workspaceContext || sowSession || researchMode !== 'internal' || !auth.account?.stableId || session.status !== 'signedIn' || session.user.role === 'customer' || access.selected?.tenantId !== record.context.tenantId || access.selected?.entityId !== record.context.entityId)
      return <p role="alert">This shared chat requires the matching staff record context.</p>;
    return <SharedAgentShell presentation={presentation} identity={boundary} actorId={auth.account.stableId} record={record} compact={compact} fillHeight={fillHeight} onLeaveGuardChange={onLeaveGuardChange} />;
  }
  return <ScopedAgentConversation presentation={presentation} recordIds={recordIds} key={boundary + ':' + researchMode + ':' + (sowSession?.conversationId ?? '') + ':' + (workspaceContext?.snapshotId ?? '')} fillHeight={fillHeight} chatOnly={chatOnly} scopeContainer={scopeContainer} onWorkResumeReceiverChange={onWorkResumeReceiverChange} onToolRequest={onToolRequest} onDraftReceiverChange={onDraftReceiverChange} onLeaveGuardChange={onLeaveGuardChange} workspaceContext={workspaceContext} sowSession={sowSession} sowBlocked={sowBlocked} onSowChange={onSowChange} compact={compact} identity={identity} researchMode={researchMode} initialMessage={initialMessage} starterContext={starterContext} onActionFinal={onActionFinal} />;
}

function ScopedAgentConversation({ presentation, recordIds, starterContext, compact, identity, researchMode, initialMessage, onToolRequest, onDraftReceiverChange, onActionFinal, sowSession, sowBlocked, onSowChange, workspaceContext, onLeaveGuardChange, fillHeight, chatOnly, scopeContainer, onWorkResumeReceiverChange }: AgentConversationProps & { compact: boolean; identity: string; researchMode: 'internal' | 'public_research' }) {
  const location = useLocation();
  const auth = useAuth();
  const navigate = useGuardedNavigate();
  const composer = React.useRef<HTMLTextAreaElement>(null);
  const busyProposals = React.useRef(new Set<string>());
  const [reviewBlocked, setReviewBlocked] = React.useState(false);
  const markReviewBusy = React.useCallback((id: string, busy: boolean) => {
    if (busy) busyProposals.current.add(id); else busyProposals.current.delete(id);
    setReviewBlocked(busyProposals.current.size > 0);
  }, []);
  const [cardDrafts, setCardDrafts] = React.useState<Record<string, WorkCardValues>>({});
  const [tenderDrafts,setTenderDrafts]=React.useState<Record<string,boolean>>({});
  const markTenderDirty=React.useCallback((id:string,dirty:boolean)=>setTenderDrafts(current=>({...current,[id]:dirty})),[]);
  const cardDirty = Object.keys(cardDrafts).length > 0 || Object.values(tenderDrafts).some(Boolean);
  const [nativeChoice, setNativeChoice] = React.useState<AgentContinuation>();
  const [resumedWork, setResumedWork] = React.useState<PersonalWorkItem>();
  const [editingChoice, setEditingChoice] = React.useState<AgentContinuation>();
  const openProposals = React.useRef(new Set<string>());
  const [pendingOpenLink, setPendingOpenLink] = React.useState<string>();
  const [continuationError, setContinuationError] = React.useState<string>();
  const queryClient = useQueryClient();
  const access = useTenantEntity();
  const { mode: audience } = useWorkspace();
  const suggestion = suggestedAgentMode(location.pathname);
  const [activeScope, setActiveScope] = React.useState<AgentAccessScope | null>(() => access.selected ? {
    kind: 'entity',
    tenantId: access.selected.tenantId,
    entityId: access.selected.entityId
  } : null);
  const promptContext = promptContextForRoute(location.pathname);
  const [tenantId, setTenantId] = React.useState<string | null>(access.selected?.tenantId ?? access.tenants[0]?.id ?? null);
  const [turns, setTurns] = React.useState<AgentTurn[]>([]);
  const publicResearch = researchMode === 'public_research';
  const [publicQueries, setPublicQueries] = React.useState('');
  const [message, setMessage] = React.useState(() => publicResearch ? '' : initialMessage?.slice(0, 4000) ?? new URLSearchParams(location.search).get('prompt')?.slice(0, 4000) ?? '');
  const [conversationId, setConversationId] = React.useState<string | undefined>(sowSession?.conversationId);
  const [lastSubmission, setLastSubmission] = React.useState<AgentSubmission>();
  const [clientRequestId, setClientRequestId] = React.useState<string>();
  const sendPayload = React.useRef<AgentChatRequest | undefined>(undefined);
  const [selectedPrompt, setSelectedPrompt] = React.useState<AgentPromptSnapshot>();
  const [selectedSet, setSelectedSet] = React.useState<AgentLibraryEntry>();
  const [historyEnabled, setHistoryEnabled] = React.useState(Boolean(sowSession));
  const [messageOffset, setMessageOffset] = React.useState(0);
  const [messageLimit, setMessageLimit] = React.useState(25);
  const [historyWriteBusy, setHistoryBusy] = React.useState(false);
  const [voiceActive, setVoiceActive] = React.useState(false);
  const voiceStop = React.useRef<(() => void) | null>(null);
  const libraryDirty = React.useRef(false);
  const markLibraryDirty = React.useCallback((dirty: boolean) => { libraryDirty.current = dirty; }, []);
  const leaveGuard = React.useRef<() => boolean>(() => true);
  leaveGuard.current = () => {
    if (busyProposals.current.size || historyBusy || !allowThreadNavigation()) return false;
    voiceStop.current?.();
    recordingSession.current += 1;
    stopRef.current?.();
    stopRef.current = null;
    setListening(false);
    window.speechSynthesis?.cancel();
    return true;
  };
  React.useEffect(() => {
    onLeaveGuardChange?.(() => leaveGuard.current());
    return () => onLeaveGuardChange?.(null);
  }, [onLeaveGuardChange]);
  const railLayout = useAgentRailLayout(compact);
  const keyboardInset = useKeyboardInset();
  const desktopHistory = railLayout.chats;
  const [historyOpen, setHistoryOpen] = React.useState(false);
  React.useEffect(() => { if (!desktopHistory) setHistoryOpen(false); }, [desktopHistory]);
  const historyTrigger = React.useRef<HTMLButtonElement>(null);
  const requestAbort = React.useRef(new AbortController());
  const contextAbort = React.useRef(new AbortController());
  const [isSending, setIsSending] = React.useState(false);
  const [agentError, setAgentError] = React.useState<unknown>();
  const messageScroll = useAgentScroll(turns, isSending, agentError);
  const [contextReadiness, setContextReadiness] = React.useState<AgentContextReadiness>({ status: 'inactive' });
  const [readAloud, setReadAloud] = React.useState(false);
  const [listening, setListening] = React.useState(false);
  const [transcriptionError, setTranscriptionError] = React.useState<string>();
  const stopRef = React.useRef<(() => void) | null>(null);
  const readAloudRef = React.useRef(false);
  const isSendingRef = React.useRef(false);
  const recordingSession = React.useRef(0);
  const reserveHistoryWrite = React.useCallback((busy: boolean) => {
    if (busy) {
      voiceStop.current?.();
      recordingSession.current += 1;
      stopRef.current?.(); stopRef.current = null;
      setListening(false);
      window.speechSynthesis?.cancel();
    }
    setHistoryBusy(busy);
  }, []);
  const scopeGeneration = React.useRef(0);
  const requestSequence = React.useRef(0);
  const contextRequestSequence = React.useRef(0);
  const contextRequestInFlight = React.useRef<string | null>(null);
  const dictationHintId = React.useId();
  const contextStatusId = React.useId();

  const contextRequest = useGetAgentContext({ request: { get signal() { return contextAbort.current.signal; } } });
  const researchStatus = useGetAgentResearchStatus({ mutation: { retry: false }, request: { get signal() { return contextAbort.current.signal; } } });
  const agent = useChatWithAgent({ mutation: { retry: false }, request: { get signal() { return requestAbort.current.signal; } } });
  const attachmentIdentity = (auth.account?.stableId ?? '') + ':' + identity;
  const attachments = useAgentAttachments({ scope: activeScope, conversationId, identity: attachmentIdentity,
    eligible: !publicResearch && !sowSession && !workspaceContext, onConversation: setConversationId });
  const historyBusy = historyWriteBusy || attachments.busy || attachments.tasks.length > 0;
  useDraftNavigationGuard(Boolean(cardDirty || message.trim() || publicQueries.trim() || attachments.hasDraft), reviewBlocked || historyBusy);
  const scopeParams = agentWorkspaceParams(activeScope ?? { kind: 'entity' });
  const messageParams = { ...scopeParams, limit: messageLimit, offset: messageOffset };
  const savedConversation = useGetAgentConversation(conversationId ?? '', scopeParams, { query: {
    queryKey: [...getGetAgentConversationQueryKey(conversationId ?? '', scopeParams), identity],
    enabled: Boolean(conversationId && activeScope && historyEnabled), retry: false, staleTime: 0
  } });
  const savedMessages = useListAgentMessages(conversationId ?? '', messageParams, { query: {
    queryKey: [...getListAgentMessagesQueryKey(conversationId ?? '', messageParams), identity],
    enabled: Boolean(conversationId && activeScope && historyEnabled), retry: false, staleTime: 0
  } });
  const historyUnavailable = historyEnabled && (savedConversation.isPending || savedMessages.isPending ||
    savedConversation.isError || savedMessages.isError || savedMessages.isFetching);
  React.useEffect(() => {
    if (savedMessages.data && !isSendingRef.current && !busyProposals.current.size) {
      const stored = storedAgentTurns(savedMessages.data.items);
      setTurns(current => {
        const live = new Map(current.filter(turn => !turn.historical && turn.requestId).map(turn => [turn.requestId + ':' + turn.role, turn]));
        const merged = stored.filter(turn => !turn.id.endsWith(':status') || !live.has(turn.requestId + ':agent'))
          .map(turn => {
            const retained = live.get(turn.requestId + ':' + turn.role);
            return retained ? { ...turn, ...retained, historical: false, continuations: turn.continuations, proposals: turn.proposals } : turn;
          });
        const seen = new Set(merged.map(turn => turn.id));
        return [...merged, ...current.filter(turn => !turn.historical && !seen.has(turn.id))];
      });
    }
    if (savedMessages.isError) messageScroll.cancelEarlier();
  }, [savedMessages.data, savedMessages.isError, messageScroll.cancelEarlier, reviewBlocked]);
  React.useEffect(() => {
    if (requestAbort.current.signal.aborted) requestAbort.current = new AbortController();
    if (contextAbort.current.signal.aborted) contextAbort.current = new AbortController();
    return () => {
      scopeGeneration.current += 1;
      requestSequence.current += 1;
      contextRequestSequence.current += 1;
      contextRequestInFlight.current = null;
      requestAbort.current.abort();
      contextAbort.current.abort();
    };
  }, []);
  function refreshSavedHistory() {
    void queryClient.invalidateQueries({ queryKey: getListAgentConversationsQueryKey() });
    if (conversationId) {
      void queryClient.invalidateQueries({ queryKey: getGetAgentConversationQueryKey(conversationId, scopeParams) });
      void queryClient.invalidateQueries({ queryKey: getListAgentMessagesQueryKey(conversationId) });
    }
  }
  function allowThreadNavigation() {
    return !busyProposals.current.size && !isSendingRef.current && !historyBusy && attachments.canNavigate() && (!(cardDirty || attachments.hasDraft || libraryDirty.current || message.trim() || publicQueries.trim() || (lastSubmission && agentError)) || window.confirm('Discard the unsent draft or unresolved request and change chats? Saved chat history will be kept.'));
  }
  const draftReceiver = React.useRef<AgentDraftReceiver>(() => false);
  draftReceiver.current = incoming => {
    if (!publicResearch || !activeScope || activeScope.kind !== 'entity' ||
      activeScope.tenantId !== access.selected?.tenantId || activeScope.entityId !== access.selected?.entityId ||
      historyBusy || historyUnavailable || isSendingRef.current || Boolean(lastSubmission && agentError) ||
      !leaveGuard.current()) return false;
    setMessage(incoming.slice(0, 4000));
    setPublicQueries('');
    setSelectedPrompt(undefined);
    return true;
  };
  React.useEffect(() => {
    onDraftReceiverChange?.(incoming => draftReceiver.current(incoming));
    return () => onDraftReceiverChange?.(null);
  }, [onDraftReceiverChange]);

  function selectConversation(row?: Pick<SavedConversation, 'id'>) {
    attachments.reset();
    voiceStop.current?.();
    requestSequence.current += 1;
    requestAbort.current.abort();
    requestAbort.current = new AbortController();
    sendPayload.current = undefined;
    if (busyProposals.current.size) return;
    setResumedWork(undefined);
    setEditingChoice(undefined); openProposals.current.clear(); setPendingOpenLink(undefined); setContinuationError(undefined);
    setCardDrafts({}); setTenderDrafts({}); setNativeChoice(undefined);
    setConversationId(row?.id); setTurns([]); setMessage(''); setPublicQueries(''); setLastSubmission(undefined);
    setClientRequestId(undefined); setAgentError(undefined); setMessageOffset(0); setMessageLimit(25); messageScroll.reset();
    setHistoryEnabled(Boolean(row)); if (!desktopHistory) setHistoryOpen(false); setSelectedPrompt(undefined); setSelectedSet(undefined);
    recordingSession.current += 1; stopRef.current?.(); setListening(false);
    window.speechSynthesis?.cancel();
  }

  const resumeWork = React.useRef<WorkResumeReceiver>(async () => false);
  resumeWork.current = async (item: PersonalWorkItem) => {
    // The fresh work read is owned by the inbox/link consumer. This final local
    // check runs after that await, against the current draft and busy state.
    if (!activeScope || !sameWorkScope(activeScope, item.scope) || historyBusy ||
      busyProposals.current.size || isSendingRef.current || sowSession || workspaceContext) return false;
    if (item.source !== 'agent') {
      if (!leaveGuard.current()) return false;
      return navigate(workResumeLink(item));
    }
    if (item.id !== item.resume.conversationId) return false;
    if ((item.resume.mode ?? 'internal') !== researchMode) {
      if (!leaveGuard.current()) return false;
      return navigate(workResumeLink(item));
    }
    if (!leaveGuard.current()) return false;
    selectConversation({ id: item.id });
    setResumedWork(item);
    requestAnimationFrame(() => composer.current?.focus());
    return true;
  };
  React.useEffect(() => {
    onWorkResumeReceiverChange?.(item => resumeWork.current(item));
    return () => onWorkResumeReceiverChange?.(null);
  }, [onWorkResumeReceiverChange]);

  const clearScopeState = React.useCallback(() => {
    attachments.reset();
    voiceStop.current?.();
    scopeGeneration.current += 1;
    requestAbort.current.abort(); requestAbort.current = new AbortController();
    contextAbort.current.abort(); contextAbort.current = new AbortController();
    sendPayload.current = undefined;
    busyProposals.current.clear(); setReviewBlocked(false); openProposals.current.clear();
    setCardDrafts({}); setTenderDrafts({}); setNativeChoice(undefined);
    setEditingChoice(undefined); setPendingOpenLink(undefined); setContinuationError(undefined);
    setResumedWork(undefined);
    setMessage(''); setPublicQueries(''); setSelectedPrompt(undefined); setSelectedSet(undefined);
    setClientRequestId(undefined); setHistoryEnabled(false); setMessageOffset(0); setMessageLimit(25); setHistoryOpen(false); messageScroll.reset();
    requestSequence.current += 1;
    recordingSession.current += 1;
    stopRef.current?.();
    stopRef.current = null;
    setListening(false);
    setTranscriptionError(undefined);
    window.speechSynthesis?.cancel();
    agent.reset();
    researchStatus.reset();
    contextRequest.reset();
    contextRequestSequence.current += 1;
    contextRequestInFlight.current = null;
    setContextReadiness({ status: 'inactive' });
    setTurns([]);
    setConversationId(undefined);
    setLastSubmission(undefined);
    isSendingRef.current = false;
    setIsSending(false);
    setAgentError(undefined);
  }, [agent, contextRequest]);

  const loadContext = React.useCallback((scope: AgentAccessScope) => {
    const scopeKey = agentScopeKey(scope);
    if (contextRequestInFlight.current === scopeKey) return;
    const generation = scopeGeneration.current;
    const requestId = ++contextRequestSequence.current;
    contextRequestInFlight.current = scopeKey;
    setContextReadiness({ status: 'loading', scopeKey });
    void contextRequest.mutateAsync({ data: { accessScope: scope } }).then((result) => {
      if (contextRequestSequence.current !== requestId || scopeGeneration.current !== generation) return;
      contextRequestInFlight.current = null;
      if (agentScopeKey(result.scope) !== scopeKey) {
        setContextReadiness({
          status: 'error',
          scopeKey,
          message: 'Agent returned access for a different scope. Retry the access check.'
        });
        return;
      }
      setContextReadiness({ status: 'ready', scopeKey, data: result });
    }).catch((error: unknown) => {
      if (contextRequestSequence.current !== requestId || scopeGeneration.current !== generation) return;
      contextRequestInFlight.current = null;
      setContextReadiness({ status: 'error', scopeKey, message: contextErrorMessage(error) });
    });
  }, [contextRequest]);

  const activate = React.useCallback((kind: AgentMode) => {
    let next: AgentAccessScope | null = null;
    if (kind === 'entity' && access.selected) {
      next = { kind, tenantId: access.selected.tenantId, entityId: access.selected.entityId };
    } else if (kind === 'tenant' && tenantId) {
      next = { kind, tenantId };
    } else if (kind === 'platform' && access.platformPermissions.length > 0) {
      next = { kind };
    }
    if (!next) return;
    if (activeScope && agentScopeKey(activeScope) === agentScopeKey(next)) return;
    if (busyProposals.current.size || historyBusy || (activeScope && (cardDirty || libraryDirty.current || message.trim() || publicQueries.trim() || isSendingRef.current || (lastSubmission && agentError)) && !window.confirm('Leave this chat and change Agent mode? Unsent work will be discarded; a pending send may still finish in its original chat.'))) return;
    clearScopeState();
    if (!activeScope) setMessage(message);
    setActiveScope(next);
  }, [access.platformPermissions.length, access.selected, activeScope, clearScopeState, tenantId, message, publicQueries, historyBusy, lastSubmission, agentError, cardDirty]);

  React.useEffect(() => {
    if (!activeScope) return;
    loadContext(activeScope);
    return () => {
      recordingSession.current += 1;
      stopRef.current?.();
      window.speechSynthesis?.cancel();
    };
  // Scope activation is the only trigger; retry calls loadContext directly for the same scope.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScope?.kind, activeScope?.tenantId, activeScope?.entityId]);

  React.useEffect(() => {
    if (activeScope?.kind !== 'entity') return;
    if (!access.selected) {
      clearScopeState();
      setActiveScope(null);
      return;
    }
    if (activeScope.tenantId === access.selected.tenantId && activeScope.entityId === access.selected.entityId) return;
    clearScopeState();
    setActiveScope({ kind: 'entity', tenantId: access.selected.tenantId, entityId: access.selected.entityId });
  }, [access.selected, activeScope, clearScopeState]);

  React.useEffect(() => {
    const prompt = new URLSearchParams(location.search).get('prompt');
    if (prompt && !publicResearch) setMessage(prompt.slice(0, 4000));
  }, [location.search, publicResearch]);

  function changeMessagePage(offset: number) {
    if (busyProposals.current.size || isSendingRef.current) return;
    setTurns([]); openProposals.current.clear();
    messageScroll.reset(); setMessageLimit(25); setMessageOffset(offset);
  }
  function jumpToLatest() {
    const total = savedMessages.data?.total;
    if (historyEnabled && total && messageOffset + messageLimit < total) changeMessagePage(Math.floor((total - 1) / 25) * 25);
    messageScroll.jumpToLatest();
  }

  const activeScopeKey = activeScope ? agentScopeKey(activeScope) : null;
  const currentContextState = contextReadiness.status !== 'inactive' && contextReadiness.scopeKey === activeScopeKey
    ? contextReadiness
    : { status: 'inactive' } as const;
  const contextData = currentContextState.status === 'ready' ? currentContextState.data : null;
  const promptTargets = usePromptTargetOptions(activeScope, identity, contextData?.permissions ?? [], access.enabledModules ?? []);

  const send = React.useCallback((submission: AgentSubmission, retry = false) => {
    const trimmed = submission.message.trim();
    if (!retry && cardDirty && !submission.continuation) { setContinuationError('Keep or review your card edits before starting another request.'); return; }
    if (!retry && (!attachments.canSend() || (attachments.hasSelection && (submission.continuation || submission.clarificationAnswers || editingChoice)))) {
      attachments.setOpen(true); setContinuationError('Finish or exclude unresolved files. Deselect attachments before continuing an action or clarification.'); return;
    }
    if (!trimmed || busyProposals.current.size || sowBlocked || historyBusy || isSendingRef.current || !activeScope || !contextData || historyUnavailable || (!retry && lastSubmission && agentError)) return;
    if (!submission.voice) voiceStop.current?.();
    recordingSession.current += 1;
    stopRef.current?.();
    stopRef.current = null;
    setListening(false);
    if (!retry && !attachments.hasSelection && !publicResearch && !sowSession && !workspaceContext && !submission.clarificationAnswers &&
      !submission.continuation && !editingChoice && !submission.prompt && !selectedPrompt && !libraryDirty.current && onToolRequest &&
      activeScope.kind === 'entity' && activeScope.tenantId === access.selected?.tenantId &&
      activeScope.entityId === access.selected?.entityId) {
      const routed = onToolRequest(trimmed);
      if (routed) {
        voiceStop.current?.();
        window.speechSynthesis?.cancel();
        // Keep the original instructions available when returning from a native workflow.
        return Promise.resolve(null);
      }
    }
    const continuation = submission.continuation ?? (editingChoice?.actionId && editingChoice.revision ? { actionKey: editingChoice.actionKey, actionId: editingChoice.actionId, revision: editingChoice.revision, mode: 'details' as const } : undefined);
    const normalized = { ...submission, continuation, message: trimmed };
    const sendKey = retry && clientRequestId ? clientRequestId : crypto.randomUUID();
    setClientRequestId(sendKey);
    setLastSubmission(normalized);
    if (!retry) {
      setTurns((items) => {
        const groupId = normalized.clarificationAnswers?.groupId;
        const existing = groupId ? items.findIndex((item) => item.clarificationGroupId === groupId) : -1;
        if (existing >= 0) {
          return items.map((item, index) => index === existing ? { ...item, content: trimmed } : item);
        }
        return [...items, {
          id: sendKey + ':user', requestId: sendKey,
          role: 'user',
          content: trimmed,
          clarificationGroupId: groupId
        }];
      });
      // Keep the typed draft until the send has a confirmed outcome.
    }
    const generation = scopeGeneration.current;
    const requestId = ++requestSequence.current;
    const submissionScope = activeScope;
    isSendingRef.current = true;
    setIsSending(true);
    setAgentError(undefined);
    const payload: AgentChatRequest = retry && sendPayload.current ? sendPayload.current : {
      message: trimmed,
      ...(!sowSession && !publicResearch && activeScope.kind === 'entity' && workspaceContext ? { workspaceContext } : {}),
      researchMode,
      ...(publicResearch ? { publicSearchQueries: publicQueries.split('\n').map(query => query.trim()).filter(Boolean) } : {}),
      clientRequestId: sendKey,
      ...(conversationId ? { conversationId } : {}),
      ...(attachments.hasSelection ? { attachments: attachments.pins() } : {}),
      ...(normalized.continuation ? { continuation: normalized.continuation } : {}),
      ...((normalized.prompt ?? selectedPrompt) && !normalized.clarificationAnswers && !normalized.continuation ? { savedPrompt: { id: (normalized.prompt ?? selectedPrompt)!.id, revision: (normalized.prompt ?? selectedPrompt)!.revision } } : {}),
      interactionMode: normalized.voice || (!sowSession && readAloud) ? 'voice' : 'chat',
      accessScope: activeScope,
      ...(audience === 'all' ? {} : { audience }),
      pathname: location.pathname,
      ...(normalized.clarificationAnswers ? { clarificationAnswers: normalized.clarificationAnswers } : {}),
      ...(sowSession ? { sowSession: { id: sowSession.id, expectedRevision: sowSession.revision } } : publicResearch ? {} : { ...recordIds, ...routeContext(location.pathname) })
    };
    sendPayload.current = payload;
    return new Promise<string | null>(resolve => agent.mutate({ data: payload }, {
      onSuccess: (result) => {
        if (requestSequence.current !== requestId || scopeGeneration.current !== generation) { resolve(null); return; }
        if (result.scope.kind !== submissionScope.kind ||
          result.scope.tenantId !== (submissionScope.tenantId ?? null) ||
          result.scope.entityId !== (submissionScope.entityId ?? null)) {
          isSendingRef.current = false;
          setIsSending(false);
          setAgentError(new Error('Agent returned a response for a different access scope. Please try again.'));
          resolve(null); return;
        }
        isSendingRef.current = false;
        setIsSending(false);
        setConversationId(result.conversationId);
        if (result.sow) onSowChange?.(result.sow);
        sendPayload.current = undefined;
        setMessage(''); setSelectedPrompt(undefined); setClientRequestId(undefined); setLastSubmission(undefined);
        setEditingChoice(normalized.continuation?.mode === 'details' && !normalized.continuation.fields ? result.continuations?.[0] : undefined);
        if (normalized.continuation?.actionId) {
          const key = normalized.continuation.actionId + ':' + normalized.continuation.revision;
          setCardDrafts(current => { const next = { ...current }; delete next[key]; return next; });
        }
        if (normalized.nativeAfterDetails) setNativeChoice(result.continuations?.find(choice => choice.actionId === normalized.continuation?.actionId));
        openProposals.current.clear();
        if (!retry && normalized.openResult && normalized.continuation?.mode === 'review') {
          result.proposals.filter(p => p.actionKey === normalized.continuation?.actionKey && p.status === 'prepared' && !p.executionId).forEach(p => openProposals.current.add(p.id));
        }
        if (historyEnabled && savedMessages.data) {
          setMessageLimit(25); messageScroll.cancelEarlier();
          setMessageOffset(Math.floor(savedMessages.data.total / 25) * 25);
        }
        refreshSavedHistory();
        const answeredGroupId = normalized.clarificationAnswers?.groupId;
        setTurns((items) => [...items.map((item) => answeredGroupId && item.clarification?.id === answeredGroupId
          ? { ...item, clarificationAnswered: true, superseded: true, continuations: [] }
          : { ...item, superseded: true, continuations: [] }), {
          id: sendKey + ':agent', requestId: sendKey,
          role: 'agent',
          content: result.reply,
          webEvidence: result.webEvidence,
          sources: result.sources,
          draftHandoffs: result.draftHandoffs,
          tenderPreparationId: result.tenderPreparationId,
          proposals: result.proposals,
          continuations: result.continuations,
          suggestedSteps: result.suggestedSteps,
          evidenceState: result.evidenceState,
          evidenceMessage: result.evidenceMessage,
          clarification: result.clarification
        }]);
        resolve(`${result.reply} ${result.clarification?.questions.map(question => question.label).join(' ') ?? ''}`.trim());
        if (!normalized.voice && readAloudRef.current) {
          const spokenQuestions = result.clarification?.questions.map((question) => question.label).join(' ') ?? '';
          speakBrowserReply(`${result.reply} ${spokenQuestions}`.trim(), error => {
            if (scopeGeneration.current === generation && requestSequence.current === requestId) setTranscriptionError(error);
          });
        }
      },
      onError: (error) => {
        resolve(null);
        if (requestSequence.current !== requestId || scopeGeneration.current !== generation) return;
        isSendingRef.current = false;
        setIsSending(false);
        openProposals.current.clear();
        setAgentError(error);
      }
    }));
  }, [activeScope, agent, audience, contextData, conversationId, location.pathname, readAloud, historyUnavailable, clientRequestId, selectedPrompt, lastSubmission, agentError, historyEnabled, savedMessages.data, researchMode, publicResearch, publicQueries, sowSession, sowBlocked, onSowChange, workspaceContext, historyBusy, onToolRequest, access.selected, editingChoice, cardDirty, attachments]);

  function submitCard(choice: AgentContinuation, mode: 'details' | 'review' | 'cancel', fields: WorkCardValues, native = false) {
    if (!choice.actionId || !choice.revision) return;
    if (message.trim() || selectedPrompt || libraryDirty.current) {
      setContinuationError('Send or clear your unsent details before updating this card.');
      composer.current?.focus(); return;
    }
    setContinuationError(undefined);
    void send({ message: mode === 'review' ? 'Prepare an exact review of these details' : mode === 'cancel' ? 'Cancel this action' : 'Keep these exact details',
      nativeAfterDetails: native,
      continuation: { actionKey: choice.actionKey, actionId: choice.actionId, revision: choice.revision, mode, ...(mode === 'cancel' ? {} : { fields }) } });
  }
  React.useEffect(() => {
    if (!nativeChoice || cardDirty || isSending || reviewBlocked) return;
    setNativeChoice(undefined);
    continueOnScreen(nativeChoice);
    // Wait for committed details and cleared draft guards before staging private values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeChoice, cardDirty, isSending, reviewBlocked]);

  function chooseDetails(choice: AgentContinuation) {
    if (reviewBlocked || historyBusy || isSendingRef.current || historyUnavailable || (lastSubmission && agentError)) return;
    voiceStop.current?.();
    setSelectedPrompt(undefined); setEditingChoice(choice); setContinuationError(undefined);
    composer.current?.focus();
    if (!message.trim() && choice.actionId && choice.revision) {
      void send({ message: 'More details', continuation: { actionKey: choice.actionKey, actionId: choice.actionId, revision: choice.revision, mode: 'details' } })?.then(() => composer.current?.focus());
    }
  }
  function continueOnScreen(choice: AgentContinuation) {
    if (reviewBlocked || isSendingRef.current || historyBusy || historyUnavailable || (lastSubmission && agentError)) return;
    setContinuationError(undefined);
    if (choice.workCard?.state === 'completed') {
      const link = choice.workCard.execution && confirmedResultLink(choice.workCard.execution);
      if (link) navigate(link);
      else setContinuationError('Check the original save receipt below to open the confirmed result.');
      return;
    }
    navigate(choice.destination, () => {
      if (choice.prefill && !stageAgentNativePrefill({
        actorId: auth.account?.stableId ?? '', tenantId: activeScope?.tenantId ?? '', entityId: activeScope?.entityId ?? ''
      }, choice)) {
        setContinuationError('A previous form draft is still waiting, or this scope changed. Finish that draft before opening another.');
        return false;
      }
      voiceStop.current?.();
      setMessage(''); setEditingChoice(undefined);
      return true;
    });
  }
  function createAndOpen(choice: AgentContinuation) {
    if (message.trim() || selectedPrompt || libraryDirty.current) {
      setContinuationError('Send or clear your unsent details before preparing this review.');
      composer.current?.focus(); return;
    }
    if (!choice.actionId || !choice.revision) return;
    setContinuationError(undefined);
    void send({ message: 'Review this draft for creation and opening', openResult: true,
      continuation: { actionKey: choice.actionKey, actionId: choice.actionId, revision: choice.revision, mode: 'review' } });
  }
  function openConfirmedResult(execution: AgentActionExecution) {
    const link = confirmedResultLink(execution);
    if (!link || execution.replayed !== false) return;
    setPendingOpenLink(link);
  }
  React.useEffect(() => {
    if (!pendingOpenLink || reviewBlocked) return;
    setPendingOpenLink(undefined);
    navigate(pendingOpenLink);
    // This intent exists only for the fresh confirmation callback, never history or recovery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenLink, reviewBlocked]);

  React.useEffect(() => {
    if (!sowBlocked) return;
    recordingSession.current += 1; stopRef.current?.(); stopRef.current = null; setListening(false);
  }, [sowBlocked]);

  function usePrompt(prompt: AgentPromptSnapshot, run = false) {
    voiceStop.current?.();
    if (busyProposals.current.size || historyBusy || isSendingRef.current || !contextData || historyUnavailable || (lastSubmission && agentError)) return false;
    if (prompt.content.length > 4000) { setTranscriptionError('This prompt exceeds 4000 characters. Edit it before use; nothing was truncated.'); return false; }
    if (message.trim() && message !== prompt.content && !window.confirm(run ? 'Replace your current draft and run this saved prompt?' : 'Replace your current draft with this saved prompt?')) return false;
    recordingSession.current += 1; stopRef.current?.(); stopRef.current = null; setListening(false);
    setSelectedPrompt(prompt); setMessage(prompt.content);
    if (run) send({ message: prompt.content, prompt });
    return true;
  }

  const starterPromptBlocked = attachments.blocked || cardDirty || reviewBlocked || voiceActive || sowBlocked || historyBusy || isSending ||
    !contextData || historyUnavailable || Boolean(selectedPrompt || editingChoice || (lastSubmission && agentError)) ||
    (publicResearch && (publicQueries.split('\n').filter(query => query.trim()).length < 1 || publicQueries.split('\n').filter(query => query.trim()).length > 3));

  function runStarterPrompt(prompt: string) {
    if (starterPromptBlocked || isSendingRef.current || busyProposals.current.size) return;
    if (libraryDirty.current) {
      setContinuationError('Save or cancel your prompt edits before starting another request.');
      return;
    }
    if (message.trim() && message !== prompt && !window.confirm('Replace your current draft and send this starter prompt?')) return;
    setMessage(prompt);
    void send({ message: starterContext ? `${prompt}\n\n(${starterContext})` : prompt });
  }

  function toggleListening() {
    voiceStop.current?.();
    if (listening) {
      stopRef.current?.();
      stopRef.current = null;
      setListening(false);
      return;
    }
    if (busyProposals.current.size || sowBlocked || historyBusy || !contextData || isSendingRef.current || historyUnavailable || (lastSubmission && agentError) || !browserTranscriptionSupported()) return;
    const sessionId = ++recordingSession.current;
    setTranscriptionError(undefined);
    setListening(true);
    const stop = startBrowserTranscription((text) => {
      if (recordingSession.current !== sessionId) return;
      setSelectedPrompt(undefined);
      setMessage(previous => [previous, text].filter(Boolean).join(' ').slice(0, 4000));
    }, () => {
      if (recordingSession.current !== sessionId) return;
      recordingSession.current += 1;
      stopRef.current = null;
      setListening(false);
    }, (error) => {
      if (recordingSession.current === sessionId) setTranscriptionError(error);
    });
    stopRef.current = stop;
    if (!stop) setListening(false);
  }

  function setReadAloudPreference(checked: boolean) {
    readAloudRef.current = checked;
    setReadAloud(checked);
    if (!checked) window.speechSynthesis?.cancel();
  }

  const canEntity = Boolean(access.selected);
  const canTenant = access.tenants.some((tenant) => (tenant.permissions?.length ?? 0) > 0);
  const canPlatform = access.platformPermissions.length > 0;
  const canManagePlatformTokens = access.platformPermissions.includes('platform.tokens.manage');
  const suggestionAvailable = suggestion === 'entity' ? canEntity : suggestion === 'tenant' ? canTenant : canPlatform;
  const transcriptionSupported = browserTranscriptionSupported();
  const synthesisSupported = browserSpeechSynthesisSupported();
  const needsAgentFunding = activeScope?.kind === 'platform' && contextData !== null && contextData.billing.balance !== null
    && contextData.billing.actionCost > contextData.billing.balance;
  const insufficientUsageTokens = errorCode(agentError) === 'INSUFFICIENT_USAGE_TOKENS';
  const availableActionCount = contextData?.capabilities.filter((item) => item.available).length ?? 0;
  const contextHeading = currentContextState.status === 'ready'
    ? currentContextState.data.label
    : currentContextState.status === 'loading'
      ? 'Checking access…'
      : currentContextState.status === 'error'
        ? 'Access check failed'
        : 'Choose a mode';
  const contextStatus = currentContextState.status === 'ready'
    ? `Agent is ready in ${currentContextState.data.label}.`
    : currentContextState.status === 'loading'
      ? `Checking access for the active ${activeScope?.kind ?? 'Agent'} scope…`
      : currentContextState.status === 'error'
        ? currentContextState.message
        : 'Choose and activate an Agent mode to enable chat.';

  const scopeControls = (
      <section className="shrink-0 border-b" aria-label="Agent access scope">
        <details className="group" open={!activeScope || undefined}>
        <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring"><BotIcon className="size-4" /><span className="min-w-0 flex-1 truncate">{contextHeading}</span><span className="text-xs text-muted-foreground">Scope & settings</span></summary>
        <div className="max-h-[35dvh] space-y-2 overflow-y-auto border-t p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs font-medium">Active Agent scope</p>
            <p className="text-xs text-muted-foreground">Choose the scope for this conversation.</p>
          </div>
          {activeScope ? <Badge variant="outline">{activeScope.kind}</Badge> : null}
          {!sowSession && !workspaceContext && <AgentEntityPicker disabled={reviewBlocked || historyBusy || isSending} />}
        </div>
        <div className={cn('flex gap-2', !compact && 'flex-wrap')} role="group" aria-label="Agent mode">
          <Button className={compact ? 'min-w-0 flex-1 px-2 sm:flex-none' : undefined} size="sm" variant={activeScope?.kind === 'entity' ? 'secondary' : 'outline'} disabled={!canEntity || Boolean(sowSession)} onClick={() => activate('entity')}>Entity</Button>
          <Button className={compact ? 'min-w-0 flex-1 px-2 sm:flex-none' : undefined} size="sm" variant={activeScope?.kind === 'tenant' ? 'secondary' : 'outline'} disabled={!canTenant || publicResearch || Boolean(sowSession) || Boolean(workspaceContext)} onClick={() => activate('tenant')}>Tenant</Button>
          <Button className={compact ? 'min-w-0 flex-1 px-2 sm:flex-none' : undefined} size="sm" variant={activeScope?.kind === 'platform' ? 'secondary' : 'outline'} disabled={!canPlatform || publicResearch || Boolean(sowSession) || Boolean(workspaceContext)} onClick={() => activate('platform')}>Platform</Button>
        </div>
        {!sowSession && !workspaceContext && canTenant && access.tenants.length > 1 ? (
          <label className="flex min-w-0 items-center gap-2 text-xs">
            <span className="shrink-0">Tenant target</span>
            <select
              className="h-8 min-w-0 max-w-full flex-1 truncate rounded-md border bg-background px-2"
              value={tenantId ?? ''}
              disabled={reviewBlocked || historyBusy || isSending}
              onChange={(event) => {
                if (busyProposals.current.size || historyBusy || isSendingRef.current || (activeScope?.kind === 'tenant' && (cardDirty || libraryDirty.current || message.trim() || publicQueries.trim()) && !window.confirm('Discard the unsent draft and change tenant?'))) return;
                setTenantId(event.target.value);
                if (activeScope?.kind === 'tenant') {
                  clearScopeState();
                  setActiveScope({ kind: 'tenant', tenantId: event.target.value });
                }
              }}
            >
              {access.tenants.filter((tenant) => (tenant.permissions?.length ?? 0) > 0).map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
            </select>
          </label>
        ) : null}
        {!sowSession && !workspaceContext && !publicResearch && suggestionAvailable && suggestion !== activeScope?.kind ? (
          <div className="flex items-center justify-between gap-2 rounded border bg-background p-2 text-xs">
            <span>{compact ? `Suggested here: ${suggestion}. Activation is explicit.` : `This page suggests ${suggestion} mode. Your active scope has not changed.`}</span>
            <Button size="sm" variant="outline" onClick={() => activate(suggestion)}>Activate {suggestion}</Button>
          </div>
        ) : null}
        {contextData ? (
          <>
            {compact ? (
              <details className="rounded border bg-background px-2 py-1.5 text-xs text-muted-foreground">
                <summary className="cursor-pointer font-medium text-foreground">
                  Scope details · {contextData.billing.balance === null ? 'Balance unavailable' : `${contextData.billing.balance} tokens`} · {contextData.billing.actionCost}/turn
                </summary>
                <div className="mt-2 space-y-2 border-t pt-2">
                  <p><span>{contextData.billing.label}</span><span> · {availableActionCount} available actions</span></p>
                  <ul className="list-disc pl-5">{contextData.capabilities.map((item) => (
                    <li key={item.key}>{item.label} — {item.available ? item.kind.replace('_', ' ') : item.unavailableReason}</li>
                  ))}</ul>
                </div>
              </details>
            ) : (
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{contextData.billing.label}</span>
                <span>Balance: {contextData.billing.balance ?? 'Unavailable'}</span>
                <span>Agent turn: {contextData.billing.actionCost} tokens</span>
                <details><summary className="cursor-pointer">Available actions ({availableActionCount})</summary>
                  <ul className="mt-1 list-disc pl-5">{contextData.capabilities.map((item) => (
                    <li key={item.key}>{item.label} — {item.available ? item.kind.replace('_', ' ') : item.unavailableReason}</li>
                  ))}</ul>
                </details>
              </div>
            )}
            {needsAgentFunding ? (
              <p className="rounded-md border bg-background p-2 text-xs text-muted-foreground" role="status">
                Agent access is ready. This turn costs {contextData.billing.actionCost} tokens, but {contextData.billing.label} has {contextData.billing.balance}. The displayed balance cannot currently cover this turn; funding will be checked again when you send.
              </p>
            ) : null}
          </>
        ) : null}
        {!sowSession && !workspaceContext && activeScope && <section aria-label="Agent work shortcuts" className="space-y-3 border-t pt-3">
          <h3 className="text-sm font-medium">Work shortcuts</h3>
          <div>
            <PersonalWorkDrawer scope={activeScope} onResume={item => resumeWork.current(item)} />
            <p className="text-xs text-muted-foreground">Resume your saved drafts, reviews and unfinished conversations.</p>
          </div>
          {activeScope.kind === 'entity' && !publicResearch && contextData && <div>
            <FollowUpDrawer disabled={reviewBlocked || historyBusy || isSending || voiceActive} />
            <p className="text-xs text-muted-foreground">See suggested next steps from existing records. You choose what to review.</p>
          </div>}
        </section>}
        </div>
        </details>
        {needsAgentFunding && contextData && <p role="status" className="border-t px-3 py-2 text-xs text-muted-foreground">Insufficient balance: {contextData.billing.balance} tokens · {contextData.billing.actionCost}/turn. Funding is checked when you send.</p>}
      </section>
  );

  return (
    <div ref={railLayout.ref} className={cn("flex min-h-0 flex-col overflow-hidden bg-background", fillHeight ? "flex-1" : "h-[42rem] max-h-[85svh]")} data-agent-workspace>
      {scopeContainer ? createPortal(scopeControls, scopeContainer) : <div hidden={chatOnly} className={chatOnly ? "hidden" : "contents"}>{scopeControls}</div>}
      {contextData && activeScope ? <>
        {selectedSet && <section className="space-y-2 rounded-md border p-3" aria-label="Selected review set">
          <p className="text-sm font-medium">{selectedSet.title} · {selectedSet.visibility} review set · revision {selectedSet.revision}</p>
          <p className="text-xs text-muted-foreground">Ordered saved checks. Choose an individual check to use in this chat.</p>
          {selectedSet.prompts.map((prompt, index) => <Button key={prompt.id} type="button" variant="outline" size="sm" disabled={reviewBlocked || historyBusy || isSending || historyUnavailable || Boolean(lastSubmission && agentError)} onClick={() => usePrompt(prompt)}>{index + 1}. {prompt.title}</Button>)}
          <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedSet(undefined)}>Clear set</Button>
        </section>}
      </> : null}
      <div className="flex min-h-0 flex-1 flex-row">
      <div hidden={chatOnly} className={chatOnly ? "hidden" : "contents"}>
      {!sowSession && contextData && activeScope && <AgentSavedChats rail mobile={!desktopHistory} pickerOpen={historyOpen} onPickerOpenChange={setHistoryOpen} triggerRef={historyTrigger} key={'chats:' + agentScopeKey(activeScope)} scope={activeScope} researchMode={researchMode} identity={identity}
        selectedId={conversationId} selected={savedConversation.data} disabled={reviewBlocked || historyBusy || isSending}
        beforeNavigate={allowThreadNavigation} onBusyChange={reserveHistoryWrite}
        onSelect={selectConversation} onDeleted={() => selectConversation()} />}
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {resumedWork && <ResumedWorkReceipt item={resumedWork} identity={auth.account?.stableId ?? identity} />}
      {historyEnabled && <div className={cn("shrink-0 flex-wrap items-center gap-2 border-b px-3 py-1 text-xs", chatOnly && !savedMessages.isError && !savedConversation.isError ? "hidden" : "flex")}>
        <div hidden={chatOnly} className={chatOnly ? "hidden" : "contents"}>
        <span>{savedConversation.data?.title ?? 'Saved conversation'}</span>
        <Button type="button" size="sm" variant="outline" disabled={reviewBlocked || messageOffset === 0 || isSending || savedMessages.isFetching} onClick={() => changeMessagePage(Math.max(0, messageOffset - 25))}>Previous messages</Button>
        <Button type="button" size="sm" variant="outline" disabled={reviewBlocked || !savedMessages.data || messageOffset + messageLimit >= savedMessages.data.total || isSending || savedMessages.isFetching} onClick={() => changeMessagePage(messageOffset + messageLimit)}>Next messages</Button>
        <Button type="button" size="sm" variant="outline" disabled={reviewBlocked || messageOffset === 0 || messageLimit >= 100 || isSending || savedMessages.isFetching} onClick={() => { const count = Math.min(25, messageOffset); messageScroll.prepareEarlier(); setMessageOffset(messageOffset - count); setMessageLimit(messageLimit + count); }}>Load earlier messages</Button>
        <span>Up to {messageLimit} saved sends in this window; use Previous/Next for other pages.</span>
        {savedMessages.isFetching && <span role="status">Loading history…</span>}
        </div>
        {(savedMessages.isError || savedConversation.isError) && <div role="alert"><p>{workspaceError(savedMessages.error ?? savedConversation.error)}</p><Button type="button" variant="outline" size="sm" onClick={() => { void savedConversation.refetch(); void savedMessages.refetch(); }}>Retry saved conversation</Button></div>}
      </div>}
      <div className="relative min-h-0 flex-1">
      {(messageScroll.showJumpToLatest || (historyEnabled && savedMessages.data && messageOffset + messageLimit < savedMessages.data.total)) && <Button type="button" size="sm" variant="secondary" className="absolute bottom-3 right-4 z-10 rounded-full shadow-md" disabled={reviewBlocked || isSending || savedMessages.isFetching} onClick={jumpToLatest}><ArrowDownIcon className="size-3.5" />Jump to latest</Button>}
      <div ref={messageScroll.viewportRef} role="region" aria-label="Agent messages" tabIndex={0} onScroll={messageScroll.handleScroll} className={cn("h-full overflow-y-auto px-4 py-6 [overflow-anchor:none]", chatOnly && "pt-12")}>
        <div className="mx-auto w-full max-w-3xl space-y-6">
        {turns.length === 0 && (
          <AgentWelcome
            heading={presentation?.heading}
            introduction={presentation?.introduction ?? (compact ? 'Ask about this page or choose a prompt. Changes still require review.' : onToolRequest ? 'Ask about your records, search the web, review a document or open website handoffs. Every saved or administrative change is shown for exact review before execution.' : 'Ask Agent to discover, explain, or prepare work in the active scope. Every saved or administrative change is shown for exact review before execution.')}
            guidance={!compact ? presentation?.guidance ?? 'Audience persona and chat/voice controls are separate from access scope. Selecting a broader mode never grants permission.' : undefined}
            questions={presentation?.starterQuestions ?? ['What can I help you with?', 'What needs attention?', 'Prepare the safest next step']}
            compact={compact} disabled={starterPromptBlocked} onQuestion={runStarterPrompt} />
        )}
        {(!(historyEnabled && !reviewBlocked && (savedMessages.isError || savedConversation.isError)) ? turns : []).map((turn) => (
          <AgentMessageBubble key={turn.id} turnId={turn.id} role={turn.role}>
            {publicResearch && turn.role === 'agent' ? <ResearchMarkdown content={turn.content} evidence={turn.webEvidence} verifiedLinksOnly /> : turn.role === 'agent' && (turn.evidenceMessage === LIFECYCLE_NAVIGATION_EVIDENCE || turn.evidenceMessage === 'Authorized native review handoff; no operation or provider dispatch.') ? <AgentLifecycleReply content={turn.content} disabled={Boolean(turn.historical || reviewBlocked || isSending || voiceActive || historyBusy || historyUnavailable)} onNavigate={to => { navigate(to); }} /> : turn.role === 'agent' ? <AgentLinkedReply content={turn.content} sources={turn.sources} /> : <div className="whitespace-pre-wrap">{turn.content}</div>}
            {turn.webEvidence?.length ? <details className="mt-3 text-xs"><summary className="cursor-pointer">Public sources ({turn.webEvidence.length})</summary><ol className="mt-2 space-y-2">{turn.webEvidence.map((source, index) => <li key={source.id}>[{index + 1}] {source.title} · retrieved {new Date(source.retrievedAt).toLocaleString()}<p className="text-muted-foreground">{source.excerpt}</p></li>)}</ol></details> : null}
            {turn.sources ? <AgentSources items={turn.sources} privateContext={activeScope && conversationId ? { scope: activeScope, conversationId, identity: attachmentIdentity } : undefined} /> : null}
            {turn.draftHandoffs ? <AgentDraftHandoffs items={turn.draftHandoffs} /> : null}
            {turn.clarification && !turn.clarificationAnswered ? (
              <AgentClarificationQuestions
                group={turn.clarification}
                disabled={reviewBlocked || historyBusy || isSending || historyUnavailable || Boolean(lastSubmission && agentError)}
                onSubmit={(clarificationAnswers, summary) => send({ message: summary, clarificationAnswers })}
              />
            ) : null}
            {turn.suggestedSteps?.length && !turn.historical ? (
              <AgentSuggestedSteps steps={turn.suggestedSteps}
                disabled={reviewBlocked || voiceActive || historyBusy || isSending || historyUnavailable || cardDirty || Boolean(lastSubmission && agentError)}
                askDisabled={Boolean(turn.superseded)}
                onAsk={prompt => send({ message: prompt })}
                onOpen={destination => { navigate(destination); }} />
            ) : null}
            {turn.clarification && turn.clarificationAnswered ? (
              <p className="mt-3 rounded-md border bg-background p-2 text-xs text-muted-foreground" role="status">Answers sent together. Agent will use them as information only.</p>
            ) : null}
            {!publicResearch && !sowSession && !workspaceContext && turn.continuations?.map(choice => (
              <React.Fragment key={choice.actionKey + ':' + choice.actionId + ':' + choice.revision}>
              {choice.workCard ? <AgentWorkCard choice={choice} card={choice.workCard}
                values={cardDrafts[workCardKey(choice.workCard)] ?? workCardValues(choice.workCard)}
                disabled={reviewBlocked || voiceActive || historyBusy || isSending || historyUnavailable || Boolean(lastSubmission && agentError)}
                onChange={values => setCardDrafts(current => {
                  const next = { ...current }, card = choice.workCard!;
                  if (workCardChanged(card, values)) next[workCardKey(card)] = values; else delete next[workCardKey(card)];
                  return next;
                })}
                onSubmit={(mode, fields, native) => submitCard(choice, mode, fields, native)} onScreen={() => continueOnScreen(choice)} />
              : <AgentContinuationCard key={choice.actionKey + ':' + choice.actionId + ':' + choice.revision} choice={choice}
                disabled={reviewBlocked || voiceActive || historyBusy || isSending || historyUnavailable || Boolean(lastSubmission && agentError)}
                onDetails={() => chooseDetails(choice)} onScreen={() => continueOnScreen(choice)} onReview={() => createAndOpen(choice)} />}
              {choice.actionKey === 'entity.enquiries.workflows' && !turn.superseded && activeScope?.kind === 'entity' && activeScope.entityId === access.selected?.entityId && activeScope.tenantId === access.selected?.tenantId && <AgentEnquiryJourney
                actionId={choice.actionId ?? choice.actionKey} audience={audience}
                disabled={reviewBlocked || voiceActive || historyBusy || isSending || historyUnavailable || Boolean(lastSubmission && agentError)}
                onContinue={id => continueOnScreen({ ...choice, destination: '/partner-engagement/enquiries/' + encodeURIComponent(id) })} />}
              {['entity.social.editorial', 'entity.content.workflows', 'entity.content.save_asset_draft', 'entity.social.save_draft'].includes(choice.actionKey) && !turn.superseded && activeScope?.kind === 'entity' && activeScope.entityId === access.selected?.entityId && activeScope.tenantId === access.selected?.tenantId && <AgentContentJourney
                actionId={choice.actionId ?? choice.actionKey}
                disabled={reviewBlocked || voiceActive || historyBusy || isSending || historyUnavailable || Boolean(lastSubmission && agentError)}
                onContinue={destination => continueOnScreen({ ...choice, destination, prefill: undefined, workCard: undefined })} />}
              </React.Fragment>
            ))}
            {turn.tenderPreparationId && activeScope?.kind === 'entity' && <AgentTenderPreparation id={turn.tenderPreparationId} onBusy={markReviewBusy} onDirty={markTenderDirty} onCreated={id=>navigate('/manage/tenders/'+id)} disabled={(reviewBlocked && !busyProposals.current.has('tender:'+turn.tenderPreparationId)) || isSending || voiceActive || historyBusy || historyUnavailable || Boolean(message.trim())} />}
            {turn.superseded && turn.proposals?.some(proposal => proposal.status === 'prepared') && <p className="mt-3 text-xs text-muted-foreground">Earlier review replaced. Use the current action’s fresh review.</p>}
            {(turn.continuations?.some(choice => choice.workCard?.proposal) ? turn.continuations.flatMap(choice => choice.workCard?.proposal ? [choice.workCard.proposal] : []) : turn.proposals)?.filter(proposal => !turn.superseded || proposal.executionId || proposal.status !== 'prepared').map((proposal) => (
              <AgentProposalReviewCard
                key={proposal.id}
                proposal={proposal}
                initialExecution={turn.continuations?.find(choice => choice.workCard?.proposal?.id === proposal.id)?.workCard?.execution}
                historical={turn.historical || turn.superseded}
                disabled={cardDirty || reviewBlocked || isSending || voiceActive || historyBusy || historyUnavailable || Boolean(message.trim() || lastSubmission && agentError || turn.superseded)}
                openAfterSave={openProposals.current.has(proposal.id)} onOpenResult={openConfirmedResult} onBusyChange={markReviewBusy}
                onFinal={(proposalId, execution, terminalStatus) => {
                  setTurns((items) => items.map((item) => ({
                    ...item,
                    proposals: item.proposals?.map((candidate) => candidate.id === proposalId ? { ...candidate, status: execution?.status === 'succeeded' ? 'succeeded' : execution?.status === 'failed' ? 'failed' : execution ? 'executing' : terminalStatus ?? 'cancelled' } : candidate),
                    continuations: item.continuations?.map(choice => {
                      const card = choice.workCard;
                      if (card?.proposal?.id !== proposalId) return choice;
                      const pending = execution && ['queued', 'running', 'partial', 'indeterminate'].includes(execution.status);
                      return { ...choice, workCard: { ...card,
                        state: pending ? 'uncertain' : execution?.status === 'succeeded' ? 'completed' : execution ? 'failed' : terminalStatus === 'expired' || terminalStatus === 'stale' || terminalStatus === 'failed' ? terminalStatus : 'cancelled',
                        availableActions: pending ? ['check_result'] : ['open_native'],
                        proposal: { ...card.proposal, status: pending ? 'executing' : execution?.status === 'succeeded' ? 'succeeded' : execution ? 'failed' : terminalStatus ?? 'cancelled',
                          executionId: execution?.id ?? card.proposal.executionId },
                        ...(execution ? { execution } : {}) } };
                    })
                  })));
                  refreshSavedHistory();
                  onActionFinal?.();
                }}
              />
            ))}
          </AgentMessageBubble>
        ))}
        {isSending ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2Icon className="size-4 animate-spin" />Agent is retrieving authorized evidence…</div> : null}
        {agentError ? (
          <div className="rounded-md border border-destructive/40 p-3 text-sm text-destructive" role="alert">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <p>{errorMessage(agentError)}</p>
                {errorCode(agentError) ? <p className="text-xs">Code: {errorCode(agentError)}</p> : null}
                {insufficientUsageTokens ? (
                  <p className="text-xs">
                    Agent access is still ready. Funding is checked separately when a paid turn is sent; no grant, rate, or permission was changed.
                  </p>
                ) : null}
                {insufficientUsageTokens && activeScope?.kind === 'platform' ? (
                  canManagePlatformTokens ? (
                    <Button asChild variant="outline" size="sm" className="mt-1">
                      <Link to="/admin/platform?section=usage-tokens">Open Usage tokens</Link>
                    </Button>
                  ) : (
                    <p className="text-xs">Ask a platform operator with Usage Tokens permission to review the separate platform Agent allowance.</p>
                  )
                ) : null}
              </div>
              {lastSubmission ? <div className="space-y-2">
                <Button type="button" variant="outline" size="sm" disabled={reviewBlocked || historyBusy || isSending || historyUnavailable} onClick={() => send(lastSubmission, true)}>Check send outcome</Button>
                {(errorCode(agentError) === 'AGENT_PROMPT_CHANGED' || (publicResearch && ['VALIDATION','RESEARCH_WEB_QUERY_INVALID','RESEARCH_WEB_QUERY_COUNT_INVALID','RESEARCH_WEB_QUERY_PRIVATE'].includes(errorCode(agentError) ?? ''))) && <Button type="button" variant="outline" size="sm" onClick={() => {
                  sendPayload.current = undefined; setClientRequestId(undefined); setLastSubmission(undefined);
                  setAgentError(undefined); setSelectedPrompt(undefined);
                }}>{publicResearch ? 'Correct rejected public queries' : 'Correct rejected prompt'}</Button>}
                {errorCode(agentError) === 'AGENT_SEND_TERMINAL' && <Button type="button" variant="outline" size="sm" onClick={() => {
                  const draft = lastSubmission.message;
                  if (lastSubmission.clarificationAnswers && !sowSession) selectConversation();
                  else if (!conversationId) setConversationId(sendPayload.current?.clientRequestId);
                  sendPayload.current = undefined; setClientRequestId(undefined); setLastSubmission(undefined); setAgentError(undefined);
                  setMessage(draft);
                }}>{lastSubmission.clarificationAnswers && !sowSession ? 'Start new chat with this draft' : 'Start a new request'}</Button>}
              </div> : null}
            </div>
          </div>
        ) : null}
        {Object.entries(cardDrafts).filter(([key]) => !turns.some(turn => turn.continuations?.some(choice => choice.workCard && workCardKey(choice.workCard) === key))).map(([key, values]) => (
          <section key={key} aria-label="Retained card edits" className="space-y-2 rounded-md border bg-background p-3 text-xs">
            <p role="status">Your edits are retained outside this message page. Return to the action’s current message; a changed revision needs a fresh review.</p>
            <details><summary className="cursor-pointer">View retained details</summary><dl>{Object.entries(values).map(([name, value]) => <div key={name}><dt className="font-medium">{name}</dt><dd className="whitespace-pre-wrap break-words">{value}</dd></div>)}</dl></details>
            <Button type="button" variant="outline" size="sm" disabled={reviewBlocked || isSending} onClick={() => setCardDrafts(current => { const next = { ...current }; delete next[key]; return next; })}>Discard these retained edits</Button>
          </section>
        ))}
        {continuationError && <p role="alert" className="rounded-md border p-3 text-sm">{continuationError}</p>}
        </div>
      </div>

      </div>
      <form className="max-h-[55%] shrink-0 overflow-y-auto bg-gradient-to-t from-background from-70% to-background/0 px-4 pb-3 pt-4" style={keyboardInset ? { marginBottom: keyboardInset } : undefined} onSubmit={(event) => { event.preventDefault(); send({ message }); }}>
        <div className="mx-auto w-full max-w-3xl space-y-2">
        {reviewBlocked && <p role="status" className="text-xs">Check the pending save outcome before changing chats, scope or actions.</p>}
        {editingChoice && <div className="rounded-md border p-2 text-xs" role="status">Adding details for {editingChoice.prefill?.name ?? editingChoice.prefill?.title ?? 'this action'}. Previously supplied values are retained. {editingChoice.missingInputs.length ? 'Still needed: ' + editingChoice.missingInputs.join(', ') : 'You can correct a named field.'}<Button type="button" variant="ghost" size="sm" disabled={reviewBlocked || isSending || Boolean(message.trim())} onClick={() => { if (!message.trim() && editingChoice.actionId && editingChoice.revision) void send({ message: 'Cancel this action', continuation: { actionKey: editingChoice.actionKey, actionId: editingChoice.actionId, revision: editingChoice.revision, mode: 'cancel' } }); }}>Cancel action details</Button></div>}
        {publicResearch && <section className="space-y-2 rounded-md border p-3" aria-label="Public search terms">
          <p className="text-sm font-medium">Explicit public web research</p>
          <p className="text-xs text-muted-foreground">Only these public terms go to search. Enter 1–3 queries, one per line, up to 300 characters each. Do not include confidential information. This chat has no access to company records or document reviews.</p>
          <Textarea aria-label="Public search queries" value={publicQueries} maxLength={902} disabled={reviewBlocked || historyBusy || isSending || Boolean(lastSubmission && agentError)} onChange={event => setPublicQueries(event.target.value)} />
          <Button type="button" size="sm" variant="outline" disabled={!activeScope || researchStatus.isPending} onClick={() => activeScope && researchStatus.mutate({ data: { accessScope: activeScope } })}>Check search availability</Button>
          {researchStatus.data && <p role="status" className="text-xs">{researchStatus.data.state}: {researchStatus.data.message} · {researchStatus.data.limits.creditsUsedThisMonth}/{researchStatus.data.limits.monthlyCreditCap} credits this month</p>}
          {researchStatus.isError && <p role="alert" className="text-xs">{workspaceError(researchStatus.error)}</p>}
        </section>}
        {selectedPrompt && <div className="rounded-md border p-2 text-xs">Saved prompt: {selectedPrompt.title} · revision {selectedPrompt.revision}
          <Button type="button" size="sm" variant="ghost" disabled={reviewBlocked || historyBusy || isSending || Boolean(lastSubmission && agentError)} onClick={() => setSelectedPrompt(undefined)}>Detach prompt</Button>
        </div>}
        <div
          id={contextStatusId}
          className={cn(
            'flex items-center justify-between gap-3 text-xs',
            currentContextState.status === 'error' ? 'border-destructive/40 text-destructive' : 'text-muted-foreground'
          )}
          role={currentContextState.status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          <span>{contextStatus}</span>
          {currentContextState.status === 'error' && activeScope ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={contextRequestInFlight.current === activeScopeKey}
              onClick={() => loadContext(activeScope)}
            >
              Retry access check
            </Button>
          ) : null}
        </div>
        {(publicResearch || sowSession || workspaceContext) && <p className="text-xs text-muted-foreground">To attach private files, use an internal Agent chat without a document workspace. Files never transfer to public search.</p>}
        <AgentComposerInput inputRef={composer} value={message} onChange={event => { setMessage(event.target.value); setSelectedPrompt(undefined); }}
          placeholder={presentation?.messagePlaceholder ?? "Ask Agent about this scope…"} disabled={reviewBlocked || voiceActive || historyBusy || !contextData || historyUnavailable || isSending} aria-describedby={contextStatusId}
          sendDisabled={attachments.blocked || cardDirty || reviewBlocked || voiceActive || !message.trim() || historyBusy || isSending || !contextData || historyUnavailable || Boolean(lastSubmission && agentError) || (publicResearch && (publicQueries.split('\n').filter(query => query.trim()).length < 1 || publicQueries.split('\n').filter(query => query.trim()).length > 3))} />
        <AgentFooter files={!publicResearch && !sowSession && !workspaceContext && activeScope ? <AgentAttachments iconOnly key={activeScopeKey + ':' + (conversationId ?? 'empty')} controller={attachments} conversationId={conversationId} scope={activeScope} identity={attachmentIdentity} copy={presentation?.attachments}
          disabled={reviewBlocked || voiceActive || historyWriteBusy || isSending || !contextData || historyUnavailable || Boolean(lastSubmission && agentError)} /> : undefined} listening={listening} readAloud={readAloud} transcriptionSupported={transcriptionSupported} synthesisSupported={synthesisSupported}
          errors={[transcriptionError, ...(!publicResearch && !sowSession && !workspaceContext && activeScope ? attachmentFooterErrors(attachments) : [])]}
          hintId={dictationHintId} hidePlayback={Boolean(sowSession)}
          dictationDisabled={voiceActive || historyBusy || (!listening && (!contextData || isSending || historyUnavailable || Boolean(lastSubmission && agentError) || !transcriptionSupported))}
          newChatDisabled={reviewBlocked || historyBusy || isSending}
          onDictation={toggleListening} onReadAloud={() => setReadAloudPreference(!readAloud)}
          onStopReadAloud={() => { window.speechSynthesis?.cancel(); setReadAloudPreference(false); }}
          onNewChat={() => { if (allowThreadNavigation()) selectConversation(); }}
          voice={!publicResearch && !sowSession && activeScope ? {
            scope: activeScope, conversationId, disabled: attachments.hasSelection || cardDirty || !contextData || reviewBlocked || historyBusy || isSending || listening || historyUnavailable || Boolean(message.trim() || selectedPrompt || (lastSubmission && agentError)),
            blocked: attachments.hasSelection || !contextData || reviewBlocked || historyBusy || historyUnavailable || Boolean(lastSubmission && agentError),
            onConversation: setConversationId, onUtterance: async text => (await send({ message: text, voice: true })) ?? null,
            onInterrupt: () => { requestAbort.current.abort(); requestAbort.current = new AbortController(); },
            onActiveChange: setVoiceActive, stopRef: voiceStop
          } : undefined} />
        </div>
      </form>
      </div>
      <div hidden={chatOnly} className={chatOnly ? "hidden" : "contents"}>
      {!publicResearch && !sowSession && contextData && activeScope && <AgentPromptLibrary key={'prompts:' + agentScopeKey(activeScope)} scope={activeScope} identity={identity} compact={compact} rail desktop={railLayout.prompts} draftText={message} onBusyChange={reserveHistoryWrite} onDirtyChange={markLibraryDirty}
        canManageTeam={activeScope.kind === 'entity' && contextData.permissions.includes('entity.manage')}
        canManagePrompts={activeScope.kind === 'tenant' ? contextData.permissions.includes('tenant.settings.manage') : activeScope.kind === 'entity' && ['entity.manage', 'entity.records.write', 'entity.content.manage'].some(permission => contextData.permissions.includes(permission))}
        {...promptContext}
        targetOptions={promptTargets.options}
        disabled={reviewBlocked || historyBusy || isSending || historyUnavailable || Boolean(lastSubmission && agentError)}
        onPrompt={usePrompt} onSet={setSelectedSet} />}
      </div>
      </div>
    </div>
  );
}
